import React, { useState, useEffect } from 'react';
import Moment from 'react-moment';
import { useStore } from 'hooks';
import { ApplicationSupplyService } from 'services';
import { STATUS_APPLICATION_SUPPLY, WAGON_UTENSILS } from 'helpers/constants';
import { Button, ButtonClose } from 'components/ui/form';
import styles from './print.module.scss';

/** Keep in sync with body.supply-print-active in print.module.scss (@media print). */
const SUPPLY_PRINT_BODY_CLASS = 'supply-print-active';

const SupplyPrintPage = ({ match, history }) => {
  const id = match.params.id;
  const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === '1';
  const { setUser, setLoading } = useStore();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.add(SUPPLY_PRINT_BODY_CLASS);
    return () => {
      document.body.classList.remove(SUPPLY_PRINT_BODY_CLASS);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      const result = await new ApplicationSupplyService({ setUser }).getPrintForm(id);
      setLoading(false);
      if (cancelled) return;
      if (result) {
        setData(result);
      } else {
        setError('Заявка не найдена или нет доступа');
      }
    })();

    return () => { cancelled = true; };
  }, [id, setUser, setLoading]);

  useEffect(() => {
    if (!isEmbedded || !data) return undefined;

    const printFrame = window.self;
    const notifyParent = () => {
      if (window.parent !== printFrame) {
        window.parent.postMessage({ type: 'SUPPLY_PRINT_FINISHED' }, window.location.origin);
      }
    };

    printFrame.addEventListener('afterprint', notifyParent);
    printFrame.print();

    return () => printFrame.removeEventListener('afterprint', notifyParent);
  }, [data, isEmbedded]);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
      return;
    }

    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push('/applications/supply');
  };

  if (error) {
    return (
      <div className={styles.root}>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.root}>
        <p>Загрузка…</p>
      </div>
    );
  }

  const formatWeight = (v) => (v != null && !Number.isNaN(Number(v))
    ? new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(v))
    : '—');
  const withDash = (v) => {
    if (v == null) return '—';
    if (typeof v === 'number') return Number.isNaN(v) ? '—' : v;
    return String(v).trim() !== '' ? v : '—';
  };

  const { application, actions } = data;
  const archiveDate = application.isArchived || application.archivedAt;
  const aw = application.applicationWagon || {};
  const consignee = aw.userConsignee?.consignee;
  const contract = aw.contract;
  const station = aw.station;
  const product = application.contractProduct?.product;
  const executor = application.user;

  return (
    <div className={styles.root}>
      <h1 className={styles.title}>Заявка на поставку №{application.id}</h1>

      <dl className={styles.dl}>
        <dt>Номер</dt>
        <dd>{application.id}</dd>
        <dt>Дата</dt>
        <dd>{application.date ? <Moment format="DD.MM.YYYY">{application.date}</Moment> : '—'}</dd>
        <dt>Грузополучатель</dt>
        <dd>{withDash(consignee?.name)}</dd>
        <dt>Контракт</dt>
        <dd>{withDash(contract?.name)}</dd>
        <dt>Дата договора</dt>
        <dd>{contract?.date ? <Moment format="DD.MM.YYYY">{contract.date}</Moment> : '—'}</dd>
        <dt>Заявка на вагон</dt>
        <dd>{aw?.id ? `№${aw.id}` : '—'}</dd>
        <dt>Дата заявки на вагон</dt>
        <dd>{aw?.date ? <Moment format="DD.MM.YYYY">{aw.date}</Moment> : '—'}</dd>
        <dt>Исполнитель</dt>
        <dd>
          {[executor?.fio, executor?.email, executor?.phone]
            .filter(value => value && String(value).trim() !== '')
            .join(', ') || '—'}
        </dd>
        <dt>Отсрочка платежа</dt>
        <dd>
          {Number(contract?.isPaymentDeferralProvided) === 1
            ? (contract?.paymentDeferralEndDate
              ? <Moment format="DD.MM.YYYY">{contract.paymentDeferralEndDate}</Moment>
              : '—')
            : 'Нет'}
        </dd>
        <dt>Продукт</dt>
        <dd>{withDash(product?.name)}</dd>
        <dt>Станция назначения</dt>
        <dd>{withDash(station?.name)}</dd>
        <dt>Группа</dt>
        <dd>{withDash(aw?.productGroup)}</dd>
        <dt>Код ГНГ</dt>
        <dd>{withDash(product?.gng)}</dd>
        <dt>Вид вагона</dt>
        <dd>{aw?.wagon?.name ? `${aw.wagon.name}${aw.wagon.ton ? ` (${aw.wagon.ton} тонн)` : ''}` : '—'}</dd>
        <dt>ЖД код грузополучателя</dt>
        <dd>{withDash(aw?.codeGd)}</dd>
        <dt>Кол-во тонн</dt>
        <dd>{withDash(application.ton)}</dd>
        <dt>Отгружено тонн</dt>
        <dd>{formatWeight(application.shippedTon)}</dd>
        <dt>Количество вагонов</dt>
        <dd>{withDash(application.wagonCount)}</dd>
        <dt>Принадлежность вагонов</dt>
        <dd>{withDash(WAGON_UTENSILS.toString(Number(aw?.utensils)))}</dd>
        <dt>Платежное поручение за продукцию</dt>
        <dd>{withDash(application?.payments?.[0])}</dd>
        <dt>Платежное поручение за ЖД тариф</dt>
        <dd>{withDash(application?.payments?.[1])}</dd>
        <dt>Дополнительная информация по вагонам</dt>
        <dd>{withDash(application?.additional)}</dd>
        <dt>Примечание</dt>
        <dd>{withDash(application?.comment)}</dd>
        <dt>Номер ГУ-12</dt>
        <dd>{withDash(aw?.gy12)}</dd>
        <dt>Статус</dt>
        <dd>{STATUS_APPLICATION_SUPPLY.toString(application.status) || '—'}</dd>
        {archiveDate ? (
          <>
            <dt>Архив</dt>
            <dd><Moment format="DD.MM.YYYY HH:mm">{archiveDate}</Moment></dd>
          </>
        ) : null}
      </dl>

      <h2 className={styles.sectionTitle}>История действий</h2>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Дата и время</th>
            <th>Тип действия</th>
            <th>Пользователь</th>
            <th>Комментарий</th>
          </tr>
        </thead>
        <tbody>
          {actions && actions.length > 0 ? (
            actions.map((action) => (
              <tr key={action.id}>
                <td>
                  {action.createdAt ? (
                    <Moment format="DD.MM.YYYY HH:mm">{action.createdAt}</Moment>
                  ) : '—'}
                </td>
                <td>{action.eventTypeLabel || '—'}</td>
                <td>{action.user?.fio || action.user?.name || '—'}</td>
                <td>{action.comment || '—'}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4}>Нет записей</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className={`${styles.actionsBar} ${styles.noPrint}`}>
        <Button className={styles.printButton} onClick={handlePrint}>
          Печать / Сохранить в PDF
        </Button>
        <p className={styles.hint}>
          Откроется окно печати браузера: выберите принтер для печати или пункт «Сохранить как PDF» для сохранения в файл.
        </p>
        <ButtonClose className={styles.closeButton} onClick={handleClose}>
          Закрыть
        </ButtonClose>
      </div>
    </div>
  );
};

export default SupplyPrintPage;
